/*
 * Scrambled theme behaviour. Plain JavaScript, no dependencies.
 *
 * Everything that moves follows the visitor's own input (scrolling or the
 * mouse) and eases to a stop. Nothing animates on its own in a loop. Visitors
 * who prefer reduced motion get the static design (html lacks .motion-ok).
 */
(() => {
  const html = document.documentElement;
  const motionOK = html.classList.contains("motion-ok");
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const wrap = (v, len) => ((v % len) + len) % len;
  const smoothstep = (e0, e1, x) => {
    const t = clamp((x - e0) / (e1 - e0), 0, 1);
    return t * t * (3 - 2 * t);
  };
  const headerHeight = () => document.querySelector(".site-header")?.offsetHeight || 64;

  /* ---- Shared animation loop ------------------------------------------- */
  const subscribers = new Set();
  let running = false;
  let lastY = window.scrollY;
  let velocity = 0;

  function tick() {
    const y = window.scrollY;
    velocity += (y - lastY - velocity) * 0.25;
    lastY = y;
    const frame = { y, velocity, vw: window.innerWidth, vh: window.innerHeight };
    let keepGoing = Math.abs(velocity) > 0.02;
    subscribers.forEach((fn) => {
      if (fn(frame)) keepGoing = true;
    });
    if (keepGoing) requestAnimationFrame(tick);
    else {
      running = false;
      velocity = 0;
    }
  }
  function wake() {
    if (running || !subscribers.size) return;
    running = true;
    requestAnimationFrame(tick);
  }
  function subscribe(fn) {
    subscribers.add(fn);
    wake();
  }
  window.addEventListener("scroll", wake, { passive: true });
  window.addEventListener("resize", wake);

  function whenVisible(el, callback, margin = "15% 0px") {
    const observer = new IntersectionObserver(([entry]) => callback(entry.isIntersecting), { rootMargin: margin });
    observer.observe(el);
  }

  /* ---- Analytics hooks (no tracker installed by default) ---------------- */
  function track(event, props) {
    try {
      window.plausible?.(event, props ? { props } : undefined);
      window.Shopify?.analytics?.publish?.(event, props || {});
      window.dispatchEvent(new CustomEvent("scrambled:track", { detail: { event, ...props } }));
    } catch {
      /* analytics must never block a click */
    }
  }
  document.addEventListener(
    "click",
    (event) => {
      const el = event.target.closest?.("[data-track]");
      if (el) track(el.dataset.track);
    },
    true,
  );
  document.addEventListener(
    "toggle",
    (event) => {
      const el = event.target;
      if (el instanceof HTMLDetailsElement && el.open && el.dataset.faq) track("faq_open", { question: el.dataset.faq });
    },
    true,
  );

  /* ---- Mobile menu ------------------------------------------------------ */
  document.querySelectorAll("[data-menu]").forEach((menu) => {
    const button = menu.querySelector("[data-menu-button]");
    const panel = menu.querySelector("[data-menu-panel]");
    const label = menu.querySelector("[data-menu-label]");
    if (!button || !panel) return;
    const isOpen = () => button.getAttribute("aria-expanded") === "true";
    const set = (open) => {
      button.setAttribute("aria-expanded", String(open));
      panel.hidden = !open;
      if (label) label.textContent = open ? "Close menu" : "Open menu";
    };
    button.addEventListener("click", () => set(!isOpen()));
    panel.addEventListener("click", (event) => {
      if (event.target.closest("a")) set(false);
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && isOpen()) {
        set(false);
        button.focus();
      }
    });
    document.addEventListener("pointerdown", (event) => {
      if (isOpen() && !menu.contains(event.target)) set(false);
    });
  });

  /* ---- Buy dialog and consent form --------------------------------------- */
  // data-buy-open="guide" opens the guide dialog, an empty value the toolkit one.
  // Without a matching dialog the link keeps its #buy / #buy-guide href.
  const buyDialogs = document.querySelectorAll("[data-buy-dialog]");
  if (buyDialogs.length && typeof buyDialogs[0].showModal === "function") {
    let opener = null;
    document.addEventListener("click", (event) => {
      const link = event.target.closest?.("[data-buy-open]");
      if (!link) return;
      const dialog = document.querySelector(`[data-buy-dialog="${link.dataset.buyOpen || "toolkit"}"]`);
      if (!dialog) return;
      event.preventDefault();
      opener = link;
      dialog.showModal();
      dialog.querySelector('input[type="checkbox"]')?.focus();
    });
    buyDialogs.forEach((dialog) => {
      dialog.querySelector("[data-buy-close]")?.addEventListener("click", () => dialog.close());
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener("close", () => opener?.focus());
    });
  }

  document.querySelectorAll("[data-buy-form]").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (form.dataset.busy) return;
      form.dataset.busy = "true";
      form.querySelector('button[type="submit"]')?.setAttribute("aria-busy", "true");
      try {
        // One copy per order: drop anything already in the cart first.
        await fetch(`${window.Shopify?.routes?.root || "/"}cart/clear.js`, { method: "POST" });
      } catch (error) {
        /* the native submit below still works */
      }
      HTMLFormElement.prototype.submit.call(form);
    });
  });
  window.addEventListener("pageshow", () =>
    document.querySelectorAll("[data-buy-form]").forEach((form) => {
      delete form.dataset.busy;
      form.querySelector('button[type="submit"]')?.removeAttribute("aria-busy");
    }),
  );

  /* ---- Shared player volume: start at half, keep what the visitor picks --- */
  let playerVolume = 0.5;
  const startVolume = (video) => {
    video.volume = playerVolume;
    if (!video.dataset.volumeWatch) {
      video.dataset.volumeWatch = "true";
      video.addEventListener("volumechange", () => {
        if (!video.muted && video.volume > 0) playerVolume = video.volume;
      });
    }
  };

  /* ---- Showcase: one large player, the films below play in it ------------ */
  document.querySelectorAll("[data-showcase]").forEach((root) => {
    const stage = root.querySelector("[data-showcase-stage]");
    const playButton = root.querySelector("[data-showcase-play]");
    const items = [...root.querySelectorAll("[data-showcase-item]")];
    if (!stage || !playButton) return;
    const field = (name) => root.querySelector(`[data-showcase-${name}]`);
    const start = () => {
      const video = stage.querySelector("video");
      if (!video) return;
      document.querySelectorAll("[data-teaser] video, [data-showcase] video").forEach((other) => other !== video && other.pause());
      stage.dataset.started = "true";
      video.controls = true;
      video.muted = false;
      startVolume(video);
      const attempt = video.play();
      if (attempt?.catch) attempt.catch(() => {});
    };
    playButton.addEventListener("click", start);
    items.forEach((item) => {
      item.addEventListener("click", () => {
        const source = item.parentElement.querySelector("template[data-showcase-src]");
        const fresh = source?.content.querySelector("video")?.cloneNode(true);
        const current = stage.querySelector("video");
        if (!fresh || !current) return;
        current.pause();
        current.replaceWith(fresh);
        items.forEach((other) => other.setAttribute("aria-pressed", String(other === item)));
        field("no").textContent = item.dataset.no;
        field("title").textContent = item.dataset.title;
        field("sub").textContent = item.dataset.sub;
        field("time").textContent = item.dataset.time;
        field("label").textContent = `Play ${item.dataset.title}`;
        start();
        stage.scrollIntoView({ behavior: motionOK ? "smooth" : "auto", block: "nearest" });
      });
    });
  });

  /* ---- Final teaser player --------------------------------------------- */
  document.querySelectorAll("[data-teaser]").forEach((player) => {
    const video = player.querySelector("video");
    const button = player.querySelector("[data-teaser-play]");
    if (!video || !button) return;
    const sync = () => (player.dataset.playing = String(!video.paused));
    button.addEventListener("click", () => {
      document.querySelectorAll("[data-teaser] video, [data-showcase] video").forEach((other) => other !== video && other.pause());
      player.dataset.started = "true";
      video.controls = true;
      if ("silent" in player.dataset) {
        // Shown without sound (internal client content): stays muted and loops.
        video.muted = true;
        video.loop = true;
      } else {
        video.muted = false;
        startVolume(video);
      }
      const attempt = video.play();
      if (attempt?.catch) attempt.catch(() => {});
      video.focus({ preventScroll: true });
    });
    video.addEventListener("play", sync);
    video.addEventListener("pause", sync);
    video.addEventListener("ended", sync);
  });

  /* ---- Videos start loading once they are on screen --------------------- */
  // Every player ships with preload="none" so the page stays light. Once a player
  // is properly in view the browser may start buffering, so play starts smoothly.
  // Large players buffer ahead ("auto"); clips in a wall only fetch the header
  // ("metadata"), so scrolling past nine clips does not download nine films.
  if ("IntersectionObserver" in window) {
    const warmVideo = (video) => {
      const small = video.closest(".concept-item, .clip-item, .work-board-film");
      const level = small ? "metadata" : "auto";
      if (video.preload === level || video.preload === "auto") return;
      video.preload = level;
      // Nothing fetched yet (still "none"): ask the browser to start now.
      if (video.readyState === 0 && video.networkState !== HTMLMediaElement.NETWORK_LOADING) video.load();
    };
    const warm = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          warm.unobserve(entry.target);
          warmVideo(entry.target);
        });
      },
      { threshold: 0.35 },
    );
    document.querySelectorAll("[data-teaser] video, [data-showcase-stage] video").forEach((video) => warm.observe(video));
  }

  /* ---- Sticky phone checkout bar ---------------------------------------- */
  const bar = document.querySelector("[data-sticky-cta]");
  if (bar) {
    // Shown once the visitor has scrolled a screen down, hidden while a
    // section with its own buy buttons is on screen.
    let pastTop = false;
    const covering = new Set();
    const update = () => {
      const visible = pastTop && covering.size === 0;
      bar.dataset.visible = String(visible);
      bar.inert = !visible;
    };
    const onScroll = () => {
      const next = window.scrollY > window.innerHeight * 0.8;
      if (next !== pastTop) {
        pastTop = next;
        update();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    const zoneObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? covering.add(entry.target) : covering.delete(entry.target)));
      update();
    });
    ["hero-cta", "whats-inside", "viral-guide", "pricing", "final-cta", "site-footer"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) zoneObserver.observe(el);
    });
  }

  if (!motionOK || !("IntersectionObserver" in window)) return;

  /* ---- Scroll reveal (one-shot) ------------------------------------------ */
  if (!designMode) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute("data-reveal", "shown");
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px" },
    );
    const limit = window.innerHeight * 0.9;
    document.querySelectorAll('[data-reveal]:not([data-reveal="shown"])').forEach((el) => {
      if (el.getBoundingClientRect().top < limit) {
        el.setAttribute("data-reveal", "shown");
        return;
      }
      el.setAttribute("data-reveal", "pending");
      revealObserver.observe(el);
    });
    window.addEventListener("beforeprint", () =>
      document.querySelectorAll('[data-reveal="pending"]').forEach((el) => el.setAttribute("data-reveal", "shown")),
    );
  }

  /* ---- Kinetic hero: lines part on scroll, letters follow the mouse ----- */
  const hero = document.querySelector("[data-hero]");
  const title = hero?.querySelector("[data-hero-title]");
  if (hero && title) {
    const lines = [...title.querySelectorAll("[data-line]")];
    const chars = lines.flatMap((line, lineIndex) =>
      [...line.querySelectorAll(".ch")].map((el) => ({ el, line: lineIndex, x: 0, y: 0, lift: 0, tilt: 0 })),
    );
    const measure = () => {
      chars.forEach((c) => {
        let x = c.el.offsetWidth / 2;
        let y = c.el.offsetHeight / 2;
        let node = c.el;
        while (node && node !== title) {
          x += node.offsetLeft;
          y += node.offsetTop;
          node = node.offsetParent;
        }
        c.x = x;
        c.y = y;
      });
    };
    // The hero is no longer the first section: scroll progress counts from
    // the hero's own top, so it is fully visible when you arrive at it.
    let heroTop = 0;
    const measureTop = () => (heroTop = hero.getBoundingClientRect().top + window.scrollY);
    measure();
    measureTop();
    window.addEventListener("resize", () => {
      measure();
      measureTop();
    });
    document.fonts?.ready.then(() => {
      measure();
      measureTop();
    });
    window.addEventListener("load", measureTop);

    let onScreen = true;
    hero.dataset.defer = "true"; // letters wait for the hero to scroll into view
    whenVisible(hero, (v) => {
      onScreen = v;
      if (v) {
        hero.dataset.in = "true"; // starts the letter animation once
        measureTop();
        wake();
      }
    }, "0px");
    const pointer = { x: -9999, y: -9999, active: false };
    if (finePointer) {
      hero.addEventListener("pointermove", (event) => {
        pointer.x = event.clientX;
        pointer.y = event.clientY;
        pointer.active = true;
        wake();
      });
      hero.addEventListener("pointerleave", () => {
        pointer.active = false;
        wake();
      });
    }

    let progress = -1;
    subscribe(({ y, vw }) => {
      if (!onScreen) return false;
      const p = clamp((y - heroTop) / Math.max(1, hero.offsetHeight), 0, 1);
      if (p !== progress) {
        progress = p;
        hero.style.setProperty("--hp", p.toFixed(4));
      }
      if (!finePointer) return false;
      const rect = title.getBoundingClientRect();
      const radius = Math.max(140, rect.height * 0.55);
      let moving = false;
      chars.forEach((c) => {
        const shift = (c.line === 0 ? -1 : 1) * p * 0.14 * vw;
        const dx = pointer.x - (rect.left + c.x + shift);
        const dy = pointer.y - (rect.top + c.y);
        const force = pointer.active ? Math.max(0, 1 - Math.hypot(dx, dy) / radius) ** 2 : 0;
        const targetLift = -force * rect.height * 0.07;
        const targetTilt = force * clamp(-dx / radius, -1, 1) * 9;
        c.lift = lerp(c.lift, targetLift, 0.16);
        c.tilt = lerp(c.tilt, targetTilt, 0.16);
        if (Math.abs(targetLift - c.lift) > 0.05 || Math.abs(targetTilt - c.tilt) > 0.05) moving = true;
        c.el.style.translate = `0 ${c.lift.toFixed(2)}px`;
        c.el.style.rotate = `${c.tilt.toFixed(2)}deg`;
      });
      return moving;
    });
  }

  /* ---- Text bands: slide with the scroll, lean with its speed ----------- */
  document.querySelectorAll("[data-marquee]").forEach((marquee) => {
    const rows = [...marquee.querySelectorAll(".marquee-row")].map((row) => ({
      track: row.querySelector(".marquee-track"),
      set: row.querySelector(".marquee-set"),
      speed: parseFloat(row.dataset.speed) || 0.4,
      cur: null,
      width: 1,
    }));
    const measure = () => rows.forEach((r) => (r.width = r.set?.offsetWidth || 1));
    measure();
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    let onScreen = false;
    whenVisible(marquee, (v) => {
      onScreen = v;
      if (v) wake();
    });
    subscribe(({ y }) => {
      if (!onScreen) return false;
      let moving = false;
      rows.forEach((r) => {
        const target = y * r.speed;
        if (r.cur === null) r.cur = target;
        r.cur = lerp(r.cur, target, 0.12);
        const diff = target - r.cur;
        if (Math.abs(diff) > 0.3) moving = true;
        const x = -wrap(r.cur, r.width);
        const skew = clamp(diff * 0.05, -10, 10);
        r.track.style.transform = `translate3d(${x.toFixed(1)}px,0,0) skewX(${(-skew).toFixed(2)}deg)`;
      });
      return moving;
    });
  });

  /* ---- Film reel: runs with the scroll, the gate shows the frame -------- */
  document.querySelectorAll("[data-reel]").forEach((reel) => {
    const track = reel.querySelector(".reel-track");
    const frames = [...track.querySelectorAll(".frame")];
    const count = frames.filter((f) => !f.hasAttribute("aria-hidden")).length;
    if (count < 2) return;
    const timeEl = reel.querySelector("[data-reel-time]");
    const labelEl = reel.querySelector("[data-reel-label]");
    let step = 0;
    let padLeft = 0;
    let frameWidth = 0;
    const measure = () => {
      frameWidth = frames[0].offsetWidth;
      step = frames[1].offsetLeft - frames[0].offsetLeft;
      padLeft = frames[0].offsetLeft;
    };
    measure();
    window.addEventListener("resize", measure);
    reel.classList.add("is-live");

    let onScreen = false;
    let cur = null;
    let active = -1;
    const render = (vw) => {
      const setWidth = step * count;
      const start = vw / 2 - padLeft - count * step - frameWidth / 2;
      const offset = start - wrap(cur, setWidth);
      track.style.transform = `translate3d(${offset.toFixed(1)}px,0,0)`;
      const k = Math.round((vw / 2 - offset - padLeft - frameWidth / 2) / step);
      if (k !== active && frames[k]) {
        frames[active]?.classList.remove("is-active");
        frames[k].classList.add("is-active");
        active = k;
        const source = frames[k % count];
        if (timeEl) timeEl.textContent = source.querySelector(".tc")?.textContent || "";
        if (labelEl) labelEl.textContent = source.querySelector(".frame-meta span:last-child")?.textContent || "";
      }
    };
    whenVisible(reel, (v) => {
      onScreen = v;
      if (v) wake();
    });
    const targetFor = (vh) => (vh - reel.getBoundingClientRect().top) * 0.85;
    cur = Math.round(targetFor(window.innerHeight) / step) * step;
    render(window.innerWidth);
    subscribe(({ vw, vh }) => {
      if (!onScreen) return false;
      // Advance frame by frame, like a projector: each frame settles in the gate.
      const target = Math.round(targetFor(vh) / step) * step;
      cur = lerp(cur, target, 0.13);
      render(vw);
      return Math.abs(target - cur) > 0.3;
    });
  });

  /* ---- The handbook opens as you scroll --------------------------------- */
  const scene = document.querySelector("[data-book]");
  if (scene) {
    const book = scene.querySelector("[data-book-body]");
    const leaves = [...scene.querySelectorAll("[data-leaf]")].sort((a, b) => a.dataset.leaf - b.dataset.leaf);
    scene.classList.add("is-live");
    let onScreen = false;
    let cur = 0;
    const apply = (p) => {
      const c = smoothstep(0.06, 0.42, p);
      const l = smoothstep(0.56, 0.9, p);
      book.style.transform =
        `translate3d(${(-25 * (1 - c)).toFixed(3)}%,0,0) ` +
        `rotateX(${(8 + 16 * (1 - c)).toFixed(2)}deg) rotateY(${(-20 * (1 - c)).toFixed(2)}deg) rotateZ(${(-3 * (1 - c)).toFixed(2)}deg)`;
      if (leaves[0]) {
        leaves[0].style.transform = `rotateY(${(-180 * c).toFixed(2)}deg) translateZ(1.2px)`;
        leaves[0].style.setProperty("--shade", (Math.sin(Math.PI * c) * 0.35).toFixed(3));
      }
      if (leaves[1]) {
        leaves[1].style.transform = `rotateY(${(-180 * l).toFixed(2)}deg) translateZ(0.6px)`;
        leaves[1].style.setProperty("--shade", (Math.sin(Math.PI * l) * 0.35).toFixed(3));
      }
      const stepNo = c < 0.5 ? "0" : l < 0.5 ? "1" : "2";
      if (scene.dataset.step !== stepNo) scene.dataset.step = stepNo;
    };
    // Follows the middle of the book: 0 while it is low on the screen, 1 once
    // it has moved a little above the middle. It opens during normal scrolling
    // and never holds the page.
    const targetFor = (vh) => {
      const rect = scene.getBoundingClientRect();
      const middle = rect.top + rect.height / 2;
      return clamp((vh * 0.85 - middle) / (vh * 0.5), 0, 1);
    };
    cur = targetFor(window.innerHeight);
    apply(cur);
    whenVisible(scene, (v) => {
      onScreen = v;
      if (v) wake();
    }, "10% 0px");
    subscribe(({ vh }) => {
      if (!onScreen) return false;
      const target = targetFor(vh);
      cur = lerp(cur, target, 0.12);
      apply(cur);
      return Math.abs(target - cur) > 0.0005;
    });
  }

  /* ---- Typed headline (one-shot when it first appears) ------------------ */
  document.querySelectorAll(designMode ? "[data-type-none]" : "[data-type]").forEach((root) => {
    const lines = [...root.querySelectorAll("[data-type-line]")];
    if (!lines.length) return;
    const steps = [];
    lines.forEach((line, index) => {
      const text = line.textContent.trim();
      line.textContent = "";
      const lead = index === 0 ? text.match(/^\d+/) : null;
      if (lead) {
        const num = document.createElement("span");
        num.className = "type-num type-ch";
        num.textContent = lead[0];
        line.appendChild(num);
        steps.push({ el: num, count: Number(lead[0]), width: lead[0].length });
      }
      [...text.slice(lead ? lead[0].length : 0)].forEach((ch) => {
        const span = document.createElement("span");
        span.className = "type-ch";
        span.textContent = ch;
        line.appendChild(span);
        steps.push({ el: span, line: index });
      });
      if (index < lines.length - 1) steps.push({ pause: 220 });
    });
    root.dataset.typing = "true";
    // The caret is drawn on the last typed letter, so it never adds a line break.
    let last = null;
    const show = (step) => {
      step.el.classList.add("on");
      last?.classList.remove("is-caret");
      step.el.classList.add("is-caret");
      last = step.el;
    };
    const run = () => {
      let i = 0;
      const next = () => {
        if (i >= steps.length) {
          root.dataset.typing = "done";
          return;
        }
        const step = steps[i++];
        if (step.pause) return setTimeout(next, step.pause);
        if (step.count) {
          show(step);
          const start = performance.now();
          const tickNum = (now) => {
            const t = clamp((now - start) / 650, 0, 1);
            step.el.textContent = String(Math.round(step.count * (1 - (1 - t) ** 3))).padStart(step.width, "0");
            if (t < 1) requestAnimationFrame(tickNum);
            else next();
          };
          return requestAnimationFrame(tickNum);
        }
        show(step);
        setTimeout(next, step.el.textContent === " " ? 20 : 34);
      };
      next();
    };
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      setTimeout(run, 250);
    };
    // On the first screen it types straight away; lower down it waits until seen.
    if (root.getBoundingClientRect().top < window.innerHeight) start();
    else whenVisible(root.closest("h1, h2, h3") || root, (v) => v && start(), "0px");
    // Safety net: never leave the headline hidden.
    setTimeout(() => {
      if (root.dataset.typing !== "done") {
        root.querySelectorAll(".type-ch").forEach((el) => el.classList.add("on"));
        root.dataset.typing = "done";
      }
    }, started ? 6000 : 15000);
  });

  /* ---- Reel phones drift and tilt while scrolling ------------------------ */
  document.querySelectorAll("[data-reels]").forEach((reels) => {
    let on = false;
    whenVisible(reels, (v) => {
      on = v;
      if (v) wake();
    }, "10% 0px");
    subscribe(({ vh }) => {
      if (!on) return false;
      const rect = reels.getBoundingClientRect();
      const p = clamp((rect.top + rect.height / 2 - vh / 2) / vh, -1, 1);
      reels.style.setProperty("--rp", p.toFixed(4));
      return false;
    });
  });

  /* ---- Claude skill folder opens as it scrolls into view ----------------- */
  document.querySelectorAll("[data-folder]").forEach((folder) => {
    let on = false;
    whenVisible(folder, (v) => {
      on = v;
      if (v) wake();
    }, "15% 0px");
    subscribe(({ vh }) => {
      if (!on) return false;
      const top = folder.getBoundingClientRect().top;
      const p = clamp((vh * 0.95 - top) / (vh * 0.55), 0, 1);
      folder.style.setProperty("--fp", (1 - (1 - p) ** 3).toFixed(4));
      return false;
    });
  });

  /* ---- Numbers count up the first time they appear ---------------------- */
  document.querySelectorAll("[data-count]").forEach((el) => {
    const raw = el.dataset.count || el.textContent.trim();
    const time = raw.match(/^(\d+):(\d{2})$/);
    // An optional plus sign and thousands commas are kept: "+9,714" counts as 9714.
    const num = raw.match(/^(\+?)(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)(.*)$/);
    if (!time && !num) return;
    const digits = num ? num[2].replace(/,/g, "") : "";
    const total = time ? Number(time[1]) * 60 + Number(time[2]) : Number(digits);
    const prefix = num && !time ? num[1] : "";
    const suffix = num && !time ? num[3] : "";
    const grouped = !time && num[2].includes(",");
    const decimals = num && !time ? (digits.split(".")[1] || "").length : 0;
    const format = (v) =>
      time
        ? `${Math.floor(v / 60)}:${String(Math.floor(v % 60)).padStart(2, "0")}`
        : `${prefix}${grouped ? Math.round(v).toLocaleString("en-US") : v.toFixed(decimals)}${suffix}`;
    // Numbers already on screen at load only count when they ask for it
    // (data-count-delay, used on the first screen after the typed headline).
    const inView = el.getBoundingClientRect().top < window.innerHeight;
    const delay = Number(el.dataset.countDelay || 0);
    if (inView && !delay) return;
    el.textContent = format(0);
    const count = () => {
      const start = performance.now();
      const run = (now) => {
        const t = clamp((now - start) / 1500, 0, 1);
        el.textContent = t < 1 ? format(total * (1 - (1 - t) ** 4)) : raw;
        if (t < 1) requestAnimationFrame(run);
      };
      requestAnimationFrame(run);
    };
    if (inView) {
      setTimeout(count, delay);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        // data-count-stagger lets a row of numbers start one after the other.
        setTimeout(count, Number(el.dataset.countStagger || 0));
      },
      { threshold: 0.6 },
    );
    observer.observe(el);
  });

  /* ---- Gentle parallax on artwork --------------------------------------- */
  const layers = [...document.querySelectorAll("[data-parallax]")].map((el) => ({
    el,
    factor: parseFloat(el.dataset.parallax) || 0.05,
    on: false,
  }));
  layers.forEach((layer) =>
    whenVisible(layer.el, (v) => {
      layer.on = v;
      if (v) wake();
    }, "10% 0px"),
  );
  if (layers.length) {
    subscribe(({ vh }) => {
      layers.forEach((layer) => {
        if (!layer.on) return;
        const rect = layer.el.getBoundingClientRect();
        const offset = (rect.top + rect.height / 2 - vh / 2) * -layer.factor;
        layer.el.style.transform = `translate3d(0,${offset.toFixed(1)}px,0)`;
      });
      return false;
    });
  }

  /* ---- Cards tilt toward the mouse (desktop only) ----------------------- */
  if (finePointer) {
    document.querySelectorAll("[data-tilt]").forEach((el) => {
      const max = parseFloat(el.dataset.tilt) || 6;
      el.addEventListener("pointermove", (event) => {
        const rect = el.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        el.style.transform = `perspective(1100px) rotateX(${(-y * max).toFixed(2)}deg) rotateY(${(x * max).toFixed(2)}deg)`;
      });
      el.addEventListener("pointerleave", () => {
        el.style.transform = "";
      });
    });
  }
})();
